/**
 * Discontinued-swatch redirect map.
 *
 * When a swatch is dropped from a product (reps have outdated emails or
 * bookmarks), landing on ``/products/<slug>?color=<old-slug>`` would
 * silently 404 the swatch — the page loads but the specific color the
 * visitor came for isn't there.
 *
 * We register the removal here so ProductDetailPage can:
 *   1. Detect the stale ``?color=`` query.
 *   2. Toast a friendly "<Old> has been discontinued — showing <New>".
 *   3. Replace the query with the ``?color=<new>`` substitute so the
 *      correct swatch opens on mount AND the URL stays shareable.
 *
 * Keys are PRODUCT SLUG → map of OLD-COLOR-SLUG → NEW-COLOR-SLUG. Both
 * sides are kebab-case lowercase. Lookups in the page normalize the
 * incoming ``?color=`` value (strip spaces, lowercase, dashify) so
 * ``Cardinal Red``, ``cardinal-red``, and ``cardinal_red`` all match.
 */
export const DISCONTINUED_SWATCHES = {
  "premier-nop-ctg": {
    "cardinal-red": {
      replacement: "bordeaux",
      displayName: "Cardinal Red",
      replacementDisplayName: "Bordeaux",
    },
  },
  "premier-nop-tile-rg": {
    "cardinal-red-3353": {
      replacement: "bordeaux-7178",
      displayName: "Cardinal Red 3353",
      replacementDisplayName: "Bordeaux 7178",
    },
    // Short-form the beacon or an old marketing email might have used
    "cardinal-red": {
      replacement: "bordeaux-7178",
      displayName: "Cardinal Red",
      replacementDisplayName: "Bordeaux 7178",
    },
  },
};

/**
 * Normalize an arbitrary user-supplied color token to the kebab-case
 * slug we use as map keys. "Cardinal Red" → "cardinal-red", "cardinal
 * red 3353" → "cardinal-red-3353", "Cardinal_Red" → "cardinal-red".
 */
export function normalizeColorSlug(raw) {
  if (!raw) return "";
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Look up a replacement for a (product slug, incoming color token)
 * pair. Returns ``null`` when the product has no discontinued-swatch
 * rules or the color isn't on the map.
 */
export function resolveDiscontinuedSwatch(productSlug, incomingColor) {
  if (!productSlug || !incomingColor) return null;
  const perProduct = DISCONTINUED_SWATCHES[productSlug];
  if (!perProduct) return null;
  const key = normalizeColorSlug(incomingColor);
  return perProduct[key] || null;
}
